<template>
  <div class="min-h-screen w-full bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 fixed inset-0 overflow-y-auto pt-16">
    <!-- Header fusion ImportMemos + bouton retour InteractiveMap -->
    <div class="relative overflow-hidden bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200">
      <div class="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-indigo-600/5"></div>
      <div class="relative container mx-auto px-6 py-4">
        <Breadcrumbs class="mb-2" />
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center">
            <button
  class="bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 font-medium p-2 rounded-lg transition-colors duration-200 mr-4 flex items-center justify-center"
  @click="$router.push('/dashboard')"
>
  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
  </svg>
</button>
            <h1 class="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Gestion MEMoS
            </h1>
          </div>
          
         
        </div>
        <p class="mt-1 text-gray-600 text-base ml-12">Organisez, consultez et retrouvez tous vos MEMoS et collections thématiques.</p>
      </div>
    </div>
    
    <!-- Accès rapide (tabs) -->
    <div class="container mx-auto px-6 py-8">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <!-- Tab MEMos -->
        <button
          class="group w-full focus:outline-none"
          :class="activeTab === 'memos' ? 'bg-gradient-to-br from-blue-100 via-blue-50 to-white' : 'bg-white'"
          @click="activeTab = 'memos'"
        >
          <div class="relative rounded-xl shadow hover:shadow-lg border border-gray-200 transition-all h-28 flex flex-col justify-center items-center">
            <ClipboardListIcon class="w-8 h-8 text-blue-600 group-hover:scale-110 transition-transform mb-2" />
            <div class="text-lg font-semibold text-gray-900 group-hover:text-blue-600">Memos</div>
            <div class="text-sm text-gray-500">{{ stats.my }} mémos</div>
          </div>
        </button>
        <!-- Tab Collections -->
        <button
          class="group w-full focus:outline-none"
          :class="activeTab === 'collections' ? 'bg-gradient-to-br from-cyan-100 via-green-50 to-white' : 'bg-white'"
          @click="activeTab = 'collections'"
        >
          <div class="relative rounded-xl shadow hover:shadow-lg border border-gray-200 transition-all h-28 flex flex-col justify-center items-center">
            <FolderKanbanIcon class="w-8 h-8 text-cyan-600 group-hover:scale-110 transition-transform mb-2" />
            <div class="text-lg font-semibold text-gray-900 group-hover:text-cyan-600">Collections</div>
            <div class="text-sm text-gray-500">{{ collections.length }} collections</div>
          </div>
        </button>
        <!-- Partagés avec moi (placeholder) -->
        <div class="group cursor-not-allowed relative">
          <div class="relative bg-white rounded-xl shadow border border-gray-200 h-28 flex flex-col justify-center items-center opacity-60">
            <Share2Icon class="w-8 h-8 text-teal-600 mb-2" />
            <div class="text-lg font-semibold text-gray-900">Partagés avec moi</div>
            <div class="text-sm text-gray-500">À venir</div>
            <div class="absolute top-2 right-2 bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full font-medium text-xs">À venir</div>
          </div>
        </div>
      </div>

        <!-- Affichage exclusif -->
        <div v-if="activeTab === 'memos'">
          <!-- Sélecteur Mes mémos / Tous les mémos -->
          <div class="flex justify-center mb-6">
            <div class="bg-gray-100 rounded-md p-0.5 flex items-center shadow">
              <button
                class="px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200"
                :class="{ 
                  'bg-white shadow text-blue-600': viewFilter === 'my', 
                  'text-gray-600 hover:text-blue-600': viewFilter !== 'my' 
                }"
                @click="setFilter('my')"
              >
                Mes mémos
              </button>
              <button
                class="px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200"
                :class="{ 
                  'bg-white shadow text-blue-600': viewFilter === 'accessible', 
                  'text-gray-600 hover:text-blue-600': viewFilter !== 'accessible' 
                }"
                @click="setFilter('accessible')"
              >
                Tous les mémos
              </button>
            </div>
          </div>
          <div class="bg-white rounded-xl shadow border border-gray-200 p-6 mb-8">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ClipboardListIcon class="w-5 h-5 text-blue-600" />
              {{ viewFilter === 'my' ? 'Mes MEMoS' : 'Tous les MEMoS accessibles' }}
              <span class="text-sm font-normal text-gray-500">
                ({{ filteredMemos.length }} mémo{{ filteredMemos.length > 1 ? 's' : '' }})
              </span>
            </h2>
            <MemosSubset 
              :memos="filteredMemos" 
              :show-delete-button="true" 
              :show-ownership-filter="false" 
              :is-loading="isLoading"
              @view="showMemo" 
              @edit="editMemo" 
              @delete="handleDeleteMemo"
              @refresh="refreshMemos"
              @create-memo="handleCreateMemo"
              @delete-memos="handleDeleteMemos"
            />
          </div>
        </div>
        <div v-else-if="activeTab === 'collections'">
          <!-- Bouton de filtrage en haut -->
          <div class="flex items-center justify-center mb-6">
            <div class="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                class="px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200"
                :class="{ 
                  'bg-white shadow text-cyan-600': collectionsViewFilter === 'my', 
                  'text-gray-600 hover:text-cyan-600': collectionsViewFilter !== 'my' 
                }"
                @click="setCollectionsFilter('my')"
              >
                Mes collections
              </button>
              <button
                class="px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200"
                :class="{ 
                  'bg-white shadow text-cyan-600': collectionsViewFilter === 'accessible', 
                  'text-gray-600 hover:text-cyan-600': collectionsViewFilter !== 'accessible' 
                }"
                @click="setCollectionsFilter('accessible')"
              >
                Toutes les collections
              </button>
            </div>
          </div>
          
          <div class="bg-white rounded-xl shadow border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FolderKanbanIcon class="w-5 h-5 text-cyan-600" />
              {{ collectionsViewFilter === 'my' ? 'Mes collections' : 'Toutes les collections accessibles' }}
              <span class="text-sm font-normal text-gray-500">
                ({{ filteredCollections.length }} collection{{ filteredCollections.length > 1 ? 's' : '' }})
              </span>
            </h2>
            <CollectionSubset
              :items="filteredCollections"
              :is-loading="collectionsLoading"
              @create="handleCreateCollection"
              @view="handleViewCollection"
              @edit="handleEditCollection"
              @delete="handleDeleteCollection"
            />
          </div>
        </div>
      </div>
    
    <Notification ref="notification" />
    
    <!-- Modal de création/édition de collection -->
    <CollectionEditor
      v-model="showCollectionEditor"
      :collection-to-edit="selectedCollection"
      @collection-saved="handleCollectionSaved"
    />
    
    <!-- Modal de confirmation pour suppression -->
    <ModalAreYouSure
      v-model="showDeleteModal"
      title="Supprimer les mémos sélectionnés"
      :message="deleteModalMessage"
      @confirm="confirmDeleteMemos"
    />
    
    <!-- Modal de confirmation pour suppression de collection -->
    <ModalAreYouSure
      v-model="showDeleteConfirmation"
      title="Confirmer la suppression"
      :message="`Voulez-vous vraiment supprimer la collection '${collectionToDelete?.name}' ? Cette action est irréversible.`"
      confirm-text="Supprimer"
      @confirm="confirmDeleteCollection"
    />
  </div>
</template>

<script setup lang="ts">
import { deleteMemo } from '@/api/memos';
import type { MemoCollection } from '@/api/types/memos/collections';
import type { Memo } from '@/api/types/memos/types';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import CollectionEditor from '@/components/collections/CollectionEditor.vue';
import Notification from '@/components/Notification.vue';
import CollectionSubset from '@/components/subsets/CollectionSubset.vue';
import MemosSubset from '@/components/subsets/MemosSubset.vue';
import ModalAreYouSure from '@/components/utils/modal_areyousure.vue';
import { useMemos } from '@/composables/useAssets';
import { useAssetsCacheStore } from '@/stores/assetsCache';
import { useAuthStore } from '@/stores/auth';
import { useBreadcrumbsStore } from '@/stores/breadcrumbs';
import { useCollectionsStore } from '@/stores/collections';
import type { NotificationType } from '@/types/notification';
import { ClipboardListIcon, FolderKanbanIcon, Share2Icon } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const breadcrumbsStore = useBreadcrumbsStore();
const assetsCacheStore = useAssetsCacheStore();
const authStore = useAuthStore();
const notification = ref<InstanceType<typeof Notification> | null>(null);
const router = useRouter();

// État pour la suppression en lot
const memosToDelete = ref<string[]>([]);
const showDeleteModal = ref(false);
const deleteModalMessage = computed(() => {
  const count = memosToDelete.value.length;
  return `Êtes-vous sûr de vouloir supprimer ${count} mémo${count > 1 ? 's' : ''} ? Cette action est irréversible.`;
});

// État pour l'édition de collection
const showCollectionEditor = ref(false);
const selectedCollection = ref<MemoCollection | null>(null);

// État pour la suppression de collection
const collectionToDelete = ref<MemoCollection | null>(null);
const showDeleteConfirmation = ref(false);

// Utilisation du composable useMemos
const {
  filteredAssets: filteredMemos,
  isLoading,
  stats,
  viewFilter,
  setFilter,
  refreshAssets: refreshMemos,
  removeAsset: removeMemo
} = useMemos({ defaultFilter: 'my' });

const collectionsStore = useCollectionsStore();
const { collections, isLoading: collectionsLoading } = storeToRefs(collectionsStore);
const collectionsViewFilter = ref<'my' | 'accessible'>('my');

const setCollectionsFilter = (filter: 'my' | 'accessible') => {
  collectionsViewFilter.value = filter;
  console.log('🔍 GestionMemos: setCollectionsFilter', filter);
};

// Collections filtrées selon le mode sélectionné
const filteredCollections = computed(() => {
  console.log('🔍 GestionMemos: filtering collections', { 
    viewFilter: collectionsViewFilter.value, 
    totalCollections: collections.value.length,
    userId: authStore.user?.uuid 
  });
  
  switch (collectionsViewFilter.value) {
    case 'my':
      return collections.value.filter(collection => collection.user_id === authStore.user?.uuid);
    case 'accessible':
    default:
      return collections.value;
  }
});

const activeTab = ref<'memos' | 'collections'>('memos');

// Récupérer l'onglet actif depuis l'URL ou le localStorage
const getInitialTab = (): 'memos' | 'collections' => {
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get('tab') as 'memos' | 'collections';
  
  if (tabFromUrl && ['memos', 'collections'].includes(tabFromUrl)) {
    return tabFromUrl;
  }
  
  // Vérifier si on vient d'une collection (depuis le localStorage)
  const fromCollection = localStorage.getItem('gestionMemos_fromCollection');
  if (fromCollection === 'true') {
    localStorage.removeItem('gestionMemos_fromCollection');
    return 'collections';
  }
  
  return 'memos';
};

// Initialiser l'onglet actif
activeTab.value = getInitialTab();

onMounted(() => {
  console.log('🔍 GestionMemos: onMounted called');
  breadcrumbsStore.updateBreadcrumbs([
    { name: 'Outils', path: '/dashboard' },
    { name: 'Gestion MEMoS', path: '/gestion-memos' }
  ]);
  assetsCacheStore.preloadAllAssets();
  console.log('🔍 GestionMemos: calling fetchCollections');
  collectionsStore.fetchCollections();
});

// Méthodes pour gérer les actions sur les MEMoS
const showMemo = (memo: Memo): void => {
  const memoObject = JSON.parse(JSON.stringify(memo));
  router.push({
    path: `/memos/${memo.uuid}`,
    state: { memo: memoObject }
  });
};

const editMemo = (memo: Memo): void => {
  router.push({
        path: '/form',
        query: {
          mode: 'edit',
          memoId: memo.uuid,
          step: '1',
          isFromDetail: 'true'
        }
      });
};

const handleDeleteMemo = async (memo: Memo): Promise<void> => {
  try {
    await deleteMemo(memo.uuid);
    removeMemo(memo.uuid); // <-- removeMemo passe déjà par le store via useMemos
    showNotification("Mémo supprimé avec succès", "success");
  } catch (error) {
    console.error("Erreur lors de la suppression du mémo:", error);
    showNotification("Erreur lors de la suppression du mémo", "error");
  }
};

const handleCreateMemo = (): void => {
  router.push('/form');
};

const handleDeleteMemos = (memoUuids: string[]): void => {
  memosToDelete.value = memoUuids;
  showDeleteModal.value = true;
};

const confirmDeleteMemos = async (): Promise<void> => {
  let successCount = 0;
  let errorCount = 0;

  try {
    const results = await Promise.allSettled(
      memosToDelete.value.map(uuid => deleteMemo(uuid))
    );

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successCount++;
        removeMemo(memosToDelete.value[index]); // <-- removeMemo passe déjà par le store
      } else {
        errorCount++;
        console.error(`Erreur lors de la suppression du mémo ${memosToDelete.value[index]}:`, result.reason);
      }
    });

    if (successCount > 0) {
      showNotification(
        `${successCount} mémo${successCount > 1 ? 's' : ''} supprimé${successCount > 1 ? 's' : ''} avec succès`,
        "success"
      );
    }

    if (errorCount > 0) {
      showNotification(
        `Erreur lors de la suppression de ${errorCount} mémo${errorCount > 1 ? 's' : ''}`,
        "error"
      );
    }

  } catch (error) {
    console.error("Erreur lors de la suppression des mémos:", error);
    showNotification("Erreur lors de la suppression des mémos", "error");
  } finally {
    memosToDelete.value = [];
    showDeleteModal.value = false;
  }
};

// Handlers pour les collections
const handleCreateCollection = () => {
  selectedCollection.value = null;
  showCollectionEditor.value = true;
};

const handleViewCollection = (collection: MemoCollection) => {
  // Marquer qu'on vient de la gestion des mémos pour revenir sur l'onglet collections
  localStorage.setItem('gestionMemos_fromCollection', 'true');
  router.push({ name: 'CollectionView', params: { id: collection.uuid } });
};

const handleEditCollection = (collection: MemoCollection) => {
  selectedCollection.value = collection;
  showCollectionEditor.value = true;
};

const handleDeleteCollection = (collection: MemoCollection) => {
  collectionToDelete.value = collection;
  showDeleteConfirmation.value = true;
};

const confirmDeleteCollection = async () => {
  if (collectionToDelete.value) {
    const success = await collectionsStore.deleteCollection(collectionToDelete.value.uuid);
    if (success) {
      showNotification(`La collection '${collectionToDelete.value.name}' a été supprimée.`, "success");
    } else {
      showNotification(`Erreur lors de la suppression de la collection.`, "error");
    }
  }
  showDeleteConfirmation.value = false;
  collectionToDelete.value = null;
};

const handleCollectionSaved = () => {
  showCollectionEditor.value = false;
  // Le cache est déjà mis à jour dans CollectionEditor, pas besoin de recharger
};

const showNotification = (message: string, type: NotificationType): void => {
  if (notification.value) {
    notification.value.showNotification(message, type);
  }
};
</script>

<style scoped>
.min-h-screen {
  min-height: 100vh;
}
</style> 